/**
 * JournalArticlesTable Component
 *
 * Full view of journal articles table showing all available columns.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover"
import { JournalArticle, authorContactStatusLabelMap, exclusionReasonLabelMap, formatInstitutions, sourceTypeLabelMap, extractionStatusLabelMap, topicLabelMap } from "@/services/journalArticlesService"
import { BiblioCollection, isCollectionVisibleToUser } from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"
import ExtractStatusBadge from "@/components/journal-articles/ExtractStatusBadge"
import UploadStatusBadge from "@/components/journal-articles/UploadStatusBadge"
import TopicsBadges from "@/components/journal-articles/TopicsBadges"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import SortableColumnHeader from "@/components/survey-catalogue/SortableColumnHeader"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import TableViewSelector, { ViewMode } from "@/components/shared/TableViewSelector"
import { getColumnGroupsForViewMode } from "@/components/journal-articles/columns"
import { ColumnGroup } from "@/components/shared/ManageColumnsDialog"
import { TablePageSizeSelector } from "@/components/shared/TablePageSizeSelector.tsx"
import { Spinner } from "@/components/ui/spinner.tsx"
import { formatDate } from "@/utils/dateUtils"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import { MapPin, CalendarDays, X, Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { Fragment, ReactNode, useState } from "react"
import { JournalArticlesPage, PAGE_SIZE_OPTIONS } from "@/components/journal-articles/useJournalArticlesPage"

/** Column keys that support server-side sorting in the journal articles table. */
type JournalArticleSortableKey = "publicationYear" | "scorePRTopic"

/**
 * The exact subset of a page object the table consumes, so the same table can
 * be driven by either the Journal Articles page hook or the PR Literature
 * Review page hook. The two page types differ in section-specific members
 * (their `sidebarOption` union and setter, and the PR section's score controls)
 * that the table never reads; picking only the members used here keeps the
 * table decoupled from either hook's full shape. Everything the table does use
 * (list state, pagination, sort, selection, and the pagination/filter handlers)
 * is identical across both hooks.
 */
export type ArticlesTablePage = Pick<
  JournalArticlesPage,
  | "articles"
  | "loading"
  | "error"
  | "collections"
  | "sortConfig"
  | "sortEnabled"
  | "selectArticle"
  | "toggleSort"
  | "selectedArticle"
  | "totalCount"
  | "pageIndex"
  | "pageSize"
  | "totalPages"
  | "activeFilters"
  | "viewMode"
  | "setViewMode"
  | "setVisibleColumns"
  | "changePageSize"
  | "changeFilters"
  | "goToFirstPage"
  | "goToPreviousPage"
  | "goToNextPage"
  | "goToLastPage"
>

/** Set of column keys that render a clickable sort header. */
const SORTABLE_COLUMNS = new Set<string>(["publicationYear", "scorePRTopic"])

/** Props for the JournalArticlesTable component. */
interface JournalArticlesTableProps {
  /**
   * The page object from a page hook (list state, sort, handlers). Typed as the
   * widened {@link ArticlesTablePage} so either the Journal Articles or the PR
   * Literature Review page hook can drive the same table.
   */
  page: ArticlesTablePage
  /** The filter options to offer in the toolbar's filter selector for this page. */
  filterOptions: FilterOption[]
  /**
   * The set of visible column keys. Passed explicitly (rather than read from
   * `page`) so a page can strip columns it does not want (e.g. non-Open-Author-
   * Contacts pages strip the location columns).
   */
  visibleColumns: Set<string>
  /**
   * The view modes offered in the toolbar's view selector. Defaults to the
   * standard density options; the PR Bulk Review page passes its single locked
   * mode.
   */
  viewModeOptions?: ViewMode[]
  /**
   * Extra controls appended to the right of the toolbar (e.g. a page-specific
   * action). Rendered after the view selector.
   */
  toolbarTrailing?: ReactNode
  /**
   * Resolver for the column groups shown in the view selector's Manage Columns
   * dialog for a given view mode. Defaults to the journal-articles column
   * groups. The PR Literature Review pages pass their own resolver so PR Score
   * appears in the compact and status views there without affecting Journal
   * Articles.
   *
   * @param viewMode - The active view mode.
   * @returns The column groups to offer for that view mode.
   */
  columnGroupsForViewMode?: (viewMode: ViewMode) => ColumnGroup<string>[]
  /**
   * Optional handler to remove an article from the currently-viewed collection.
   * When provided (only when viewing a user collection the user can manage), an
   * extra "Remove" column is rendered after the Title column with a per-row
   * remove button. Omitted elsewhere, so the column does not appear.
   *
   * @param article - The article to remove from the current collection.
   */
  onRemoveFromCollection?: (article: JournalArticle) => void | Promise<void>
}

/** The default density view modes offered when a page does not override them. */
const DEFAULT_VIEW_MODE_OPTIONS: ViewMode[] = ["compact", "status", "full"]

/** Columns that are always visible regardless of the column selector. */
const TOOLBAR_FIXED_COLUMNS = new Set<keyof JournalArticle>(["sourceId", "title"])

/**
 * Build the standard filter options offered on most table pages: topics, the
 * collections list, and the PR/RS status filters. The only dynamic input is the
 * collections list; the rest come from static label maps.
 *
 * Pages that need extra filters (e.g. Open Author Contacts' continents /
 * countries / study years) build their own options, optionally starting from
 * this base.
 *
 * @param collections - The collections used to populate the Collections filter.
 * @returns The base filter options.
 */
export function buildCollectionFilterOptions(collections: BiblioCollection[]): FilterOption[] {
  return [
    {
      key: "topics",
      label: "Topics",
      options: Object.values(topicLabelMap),
    },
    {
      key: "collectionIds",
      label: "Collections",
      options: collections.map((collection) => collection.collectionName),
    },
    {
      key: "prExtractionStatus",
      label: "PR Extraction Status",
      options: Object.values(extractionStatusLabelMap),
    },
    {
      key: "prContactStatus",
      label: "PR Author Contact Status",
      options: Object.values(authorContactStatusLabelMap),
    },
    {
      key: "rsExtractionStatus",
      label: "RS Extraction Status",
      options: Object.values(extractionStatusLabelMap),
    },
  ]
}

/**
 * Column definition for the full table view.
 * Each entry maps a JournalArticle key to its header label and optional min-width.
 */
interface FullTableColumn {
  /** The key on JournalArticle this column displays. */
  key: string
  /** Header label text. */
  label: string
  /** Minimum width CSS class. */
  minWidth: string
}

/** All columns rendered in the full table view. */
const FULL_TABLE_COLUMNS: FullTableColumn[] = [
  { key: "title", label: "Title", minWidth: "min-w-[450px]" },
  { key: "authors", label: "Authors", minWidth: "min-w-[200px]" },
  { key: "journaltitle", label: "Journal", minWidth: "min-w-[150px]" },
  { key: "publicationYear", label: "Year", minWidth: "min-w-[60px]" },
  { key: "studyYears", label: "Study Years", minWidth: "min-w-[120px]" },
  { key: "topics", label: "Topics", minWidth: "min-w-[120px]" },
  { key: "excluded-topics", label: "Excluded Topics", minWidth: "min-w-[120px]" },
  { key: "scorePRTopic", label: "PR Score", minWidth: "min-w-[110px]" },
  { key: "collectionIds", label: "Collections", minWidth: "min-w-[150px]" },
  { key: "volume", label: "Volume", minWidth: "min-w-[80px]" },
  { key: "issue", label: "Issue", minWidth: "min-w-[80px]" },
  { key: "pages", label: "Pages", minWidth: "min-w-[80px]" },
  { key: "doi", label: "DOI", minWidth: "min-w-[120px]" },
  { key: "doiUrl", label: "DOI URL", minWidth: "min-w-[100px]" },
  { key: "pubmedId", label: "PubMed ID", minWidth: "min-w-[100px]" },
  { key: "pubmedUrl", label: "PubMed URL", minWidth: "min-w-[100px]" },
  { key: "ihmeNid", label: "IHME NID", minWidth: "min-w-[100px]" },
  { key: "mapSurveyId", label: "MAP Survey ID", minWidth: "min-w-[100px]" },
  { key: "sourceType", label: "Source Type", minWidth: "min-w-[100px]" },
  { key: "surveyType", label: "Survey Type", minWidth: "min-w-[100px]" },
  { key: "abstract", label: "Abstract", minWidth: "min-w-[200px]" },
  { key: "citation", label: "Citation", minWidth: "min-w-[200px]" },
  { key: "suggestedCitation", label: "Suggested Citation", minWidth: "min-w-[200px]" },
  { key: "url", label: "URL", minWidth: "min-w-[120px]" },
  { key: "city", label: "City", minWidth: "min-w-[100px]" },
  { key: "institution", label: "Institution", minWidth: "min-w-[120px]" },
  { key: "institutions", label: "Institutions", minWidth: "min-w-[120px]" },
  { key: "contacts", label: "Contacts", minWidth: "min-w-[120px]" },
  { key: "reportNumber", label: "Report Number", minWidth: "min-w-[100px]" },
  { key: "researchNotes", label: "Research Notes", minWidth: "min-w-[150px]" },
  { key: "usagePermission", label: "Usage Permission", minWidth: "min-w-[100px]" },
  { key: "communicationDate", label: "Communication Date", minWidth: "min-w-[100px]" },
  { key: "currentValue", label: "Current Value", minWidth: "min-w-[100px]" },
  { key: "dateUrlVisited", label: "Date URL Visited", minWidth: "min-w-[100px]" },
  { key: "createdBy", label: "Created By", minWidth: "min-w-[100px]" },
  { key: "createdDate", label: "Created Date", minWidth: "min-w-[100px]" },
  { key: "lastModifiedBy", label: "Last Modified By", minWidth: "min-w-[100px]" },
  { key: "lastModifiedDate", label: "Last Modified Date", minWidth: "min-w-[100px]" },
  { key: "continents", label: "Continents", minWidth: "min-w-[150px]" },
  { key: "countries", label: "Countries", minWidth: "min-w-[200px]" },
  { key: "prExtractionStatus", label: "PR Extraction Status", minWidth: "min-w-[180px]" },
  { key: "rsExtractionStatus", label: "RS Extraction Status", minWidth: "min-w-[180px]" },
]

/** Keys whose values are plain URLs that should render as clickable links. */
const URL_KEYS = new Set<string>(["doiUrl", "pubmedUrl", "url"])

/** Keys that should render with line-clamp styling in cells. */
const CLAMP_KEYS = new Set<string>(["authors", "abstract", "citation", "suggestedCitation", "researchNotes"])

/**
 * Full table view for journal articles.
 *
 * Displays every property of JournalArticle as its own column.
 * Columns can be filtered via the visibleColumns prop.
 *
 * @param props - Component props.
 * @param props.articles - Array of journal articles to render.
 * @param props.loading - Whether data is being fetched.
 * @param props.onArticleSelect - Callback fired when a row is clicked.
 * @param props.selectedArticleId - The sourceId of the currently selected article.
 * @param props.visibleColumns - Optional set of visible column keys.
 */
export default function JournalArticlesTable({
  page,
  filterOptions,
  visibleColumns,
  viewModeOptions = DEFAULT_VIEW_MODE_OPTIONS,
  toolbarTrailing,
  onRemoveFromCollection,
  columnGroupsForViewMode = getColumnGroupsForViewMode,
}: JournalArticlesTableProps): React.JSX.Element {
  const { user } = useAuth()

  // List state and handlers come from the shared page object. The remaining
  // table body (below) reads these locals, so destructuring here keeps that
  // code unchanged while the component now also renders its own toolbar and
  // pagination row above the table.
  const {
    articles,
    loading,
    error,
    collections,
    sortConfig,
    sortEnabled,
    selectArticle: onArticleSelect,
    toggleSort: onToggleSort,
    selectedArticle,
    totalCount,
    pageIndex,
    pageSize,
    totalPages,
    activeFilters,
    viewMode,
    setViewMode,
    setVisibleColumns,
    changePageSize,
    changeFilters,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
  } = page
  const selectedArticleId: number | undefined = selectedArticle?.sourceId

  // The 1-based inclusive range of the current page, shown in the summary.
  const fromItem: number = pageIndex * pageSize + 1
  const toItem: number = Math.min((pageIndex + 1) * pageSize, totalCount)

  /** Whether the remove-from-collection column is shown. */
  const showRemoveColumn: boolean = onRemoveFromCollection !== undefined

  /** The sourceId whose remove request is currently in flight, if any. */
  const [removingSourceId, setRemovingSourceId] = useState<number | null>(null)

  /**
   * Invoke the remove-from-collection handler for an article, tracking the
   * in-flight row so its button shows a spinner. The row's click-to-open is
   * suppressed by stopping propagation at the button.
   *
   * @param article - The article to remove from the current collection.
   * @returns A promise that resolves once the remove attempt has completed.
   */
  const handleRemoveClick = async (article: JournalArticle): Promise<void> => {
    if (onRemoveFromCollection === undefined || removingSourceId !== null) {
      return
    }
    setRemovingSourceId(article.sourceId)
    try {
      await onRemoveFromCollection(article)
    } finally {
      setRemovingSourceId(null)
    }
  }

  /**
   * Helper to check if a column should be shown.
   *
   * @param key - The column key to check.
   * @returns True if the column should be rendered.
   */
  const isVisible = (key: string): boolean => {
    if (!visibleColumns) return true
    return visibleColumns.has(key)
  }

  const collectionNameMap = new Map<number, string>(
    collections.map((c) => [c.id, c.collectionName])
  )
  /** Map of collectionId to the full collection record, for membership checks. */
  const collectionById = new Map<number, BiblioCollection>(
    collections.map((c) => [c.id, c])
  )

  const activeColumns = FULL_TABLE_COLUMNS.filter((col) => isVisible(col.key))

  const renderCollapsedCellWithTooltip = (value: string): React.ReactNode => {
    return value != null && value !== "" ? <TruncatedCell value={value} /> : "—"
  }

  const renderCellValue = (article: JournalArticle, key: string): React.ReactNode => {
    if (key === "title") {
      return (
        <span
          className={"whitespace-normal cursor-default"}
        >
          {article.title}
        </span>
      )
    }
    if( key === "journaltitle" || key === "citation") {
      const value = article[key]
      if (!value) return "—"
      return <span className="line-clamp-2">{value}</span>
    }
    if (key === "authors") {
      return renderCollapsedCellWithTooltip(article.authors?.map(a => a.name).join("; ") ?? "")
    }
    if (key === "abstract" || key === "suggestedCitation" || key === "researchNotes") {
      const value = (article[key as keyof JournalArticle] as string) ?? ""
      if (!value) return "—"
      if (key === "abstract") {
        return <span className="line-clamp-2">{value}</span>
      }
      return renderCollapsedCellWithTooltip(value)
    }
    if (key === "topics") {
      return article.topics?.length > 0 ? (
        <TopicsBadges topics={article.topics} wrap={false} />
      ) : (
        "—"
      )
    }
    if (key === "excluded-topics") {
      return article.nonTopics?.length > 0 ? (
        <TopicsBadges nonTopics={article.nonTopics} wrap={false} />
      ) : (
        "—"
      )
    }
    if (key === "collectionIds") {
      // Only show pills for collections that are public or that the current
      // user is a member of.
      const visibleCollectionIds = (article.collectionIds ?? []).filter((id) => {
        const collection = collectionById.get(id)
        if (collection === undefined) {
          return false
        }
        return isCollectionVisibleToUser(collection, user?.sub)
      })
      return visibleCollectionIds.length > 0
        ? <div className="flex flex-wrap gap-1">{visibleCollectionIds.map((id, idx) => <Badge key={idx} variant="outline">{collectionNameMap.get(id) ?? id}</Badge>)}</div>
        : "—"
    }
    if (key === "sourceType") {
      const value = article.sourceType
      if (!value) return "—"
      return sourceTypeLabelMap[value] ?? value
    }
    if (URL_KEYS.has(key)) {
      const url = article[key as "doiUrl" | "pubmedUrl" | "url"]
      if (!url) return "—"
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 break-all">
          {url}
        </a>
      )
    }
    // Continents — render as badges
    if (key === "continents") {
      const continents = article.continents
      if (!continents || continents.length === 0) return "—"
      return (
        <div className="flex flex-wrap gap-1">
          {continents.map((continent, idx) => (
            <Badge key={idx} variant="outline">{continent}</Badge>
          ))}
        </div>
      )
    }
    // Study Years — render as badges with a clickable date entities popover
    if (key === "studyYears") {
      const studyYears = article.studyYears
      const dateEntities = article.dateEntities

      // Filter out date entities that are already shown as study years
      const alreadyShown = new Set<string>(
        (studyYears ?? []).map((v) => String(v).toLowerCase())
      )
      const filteredDateEntities = (dateEntities ?? []).filter(
        (d) => !alreadyShown.has(String(d).toLowerCase())
      )
      const hasFilteredDateEntities = filteredDateEntities.length > 0

      if (!studyYears || studyYears.length === 0) {
        // No study years, but still show date entities trigger if unique ones exist
        if (!hasFilteredDateEntities) return "—"
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2 hover:text-primary/80"
                onClick={(e) => e.stopPropagation()}
                aria-label="View date entities"
              >
                <CalendarDays className="size-3.5" />
                {filteredDateEntities.length} date{filteredDateEntities.length > 1 ? "s" : ""}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 max-h-60 overflow-y-auto" align="start">
              <PopoverHeader>
                <PopoverTitle>Date Entities</PopoverTitle>
              </PopoverHeader>
              <div className="flex flex-wrap gap-1">
                {filteredDateEntities.map((date, idx) => (
                  <Badge key={idx} variant="outline">{date}</Badge>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )
      }
      return (
        <div className="flex flex-wrap items-center gap-1">
          {studyYears.map((year, idx) => (
            <Badge key={idx} variant="outline">{year}</Badge>
          ))}
          {hasFilteredDateEntities && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 ml-1 text-xs text-primary underline underline-offset-2 hover:text-primary/80"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="View date entities"
                >
                  <CalendarDays className="size-3.5" />
                  <span>{filteredDateEntities.length}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-60 overflow-y-auto" align="start">
                <PopoverHeader>
                  <PopoverTitle>Date Entities</PopoverTitle>
                </PopoverHeader>
                <div className="flex flex-wrap gap-1">
                  {filteredDateEntities.map((date, idx) => (
                    <Badge key={idx} variant="outline">{date}</Badge>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )
    }
    // Countries — render as badges with a clickable locations popover
    if (key === "countries") {
      const countries = article.countries
      const continents = article.continents
      const locations = article.locationEntities

      // Filter out locations that are already shown as continents or countries
      // so the popover only displays genuinely additional detail.
      const alreadyShown = new Set<string>([
        ...(continents ?? []).map((v) => v.toLowerCase()),
        ...(countries ?? []).map((v) => v.toLowerCase()),
      ])
      const filteredLocations = (locations ?? []).filter(
        (loc) => !alreadyShown.has(loc.toLowerCase())
      )
      const hasFilteredLocations = filteredLocations.length > 0

      if (!countries || countries.length === 0) {
        // No countries, but still show locations trigger if unique ones exist
        if (!hasFilteredLocations) return "—"
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2 hover:text-primary/80"
                onClick={(e) => e.stopPropagation()}
                aria-label="View locations"
              >
                <MapPin className="size-3.5" />
                {filteredLocations.length} location{filteredLocations.length > 1 ? "s" : ""}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 max-h-60 overflow-y-auto" align="start">
              <PopoverHeader>
                <PopoverTitle>Locations</PopoverTitle>
              </PopoverHeader>
              <div className="flex flex-wrap gap-1">
                {filteredLocations.map((location, idx) => (
                  <Badge key={idx} variant="outline">{location}</Badge>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )
      }
      return (
        <div className="flex flex-wrap items-center gap-1">
          {countries.map((country, idx) => (
            <Badge key={idx} variant="outline">{country}</Badge>
          ))}
          {hasFilteredLocations && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 ml-1 text-xs text-primary underline underline-offset-2 hover:text-primary/80"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="View locations"
                >
                  <MapPin className="size-3.5" />
                  <span>{filteredLocations.length}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-60 overflow-y-auto" align="start">
                <PopoverHeader>
                  <PopoverTitle>Locations</PopoverTitle>
                </PopoverHeader>
                <div className="flex flex-wrap gap-1">
                  {filteredLocations.map((location, idx) => (
                    <Badge key={idx} variant="outline">{location}</Badge>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )
    }
    // PR extraction status — combined cell showing all sub-fields
    if (key === "prExtractionStatus") {
      if (!article.prExtractionStatus && !article.prUploadStatus && !article.prAuthorContactStatus && !article.prExclusionReason) return "—"
      return (
        <div className="text-xs space-y-0.5 overflow-hidden">
          {(article.prExtractionStatus != null || article.prUploadStatus != null) && (
            <div className="flex flex-wrap gap-1 items-center">
              {article.prExtractionStatus != null && <ExtractStatusBadge status={article.prExtractionStatus} />}
              {article.prUploadStatus != null && <UploadStatusBadge status={article.prUploadStatus} />}
            </div>
          )}
          {article.prAuthorContactStatus != null && <div className="truncate"><span className="text-muted-foreground">Author Contact:</span> {authorContactStatusLabelMap[article.prAuthorContactStatus] ?? article.prAuthorContactStatus}</div>}
          {article.prExclusionReason != null && <div className="truncate" title={exclusionReasonLabelMap[article.prExclusionReason] ?? article.prExclusionReason}><span className="text-muted-foreground">Reason:</span> {exclusionReasonLabelMap[article.prExclusionReason] ?? article.prExclusionReason}</div>}
          {article.prExtractionFileId != null && <div><a href={`https://docs.google.com/spreadsheets/d/${article.prExtractionFileId}`} target="_blank" rel="noopener noreferrer">Extraction Sheet</a></div>}
        </div>
      )
    }
    // RS extraction status — combined cell showing all sub-fields
    if (key === "rsExtractionStatus") {
      if (!article.rsExtractionStatus && !article.rsUploadStatus) return "—"
      return (
        <div className="text-xs space-y-0.5 overflow-hidden">
          <div className="flex flex-wrap gap-1 items-center">
            {article.rsExtractionStatus != null && <ExtractStatusBadge status={article.rsExtractionStatus} />}
            {article.rsUploadStatus != null && <UploadStatusBadge status={article.rsUploadStatus} />}
          </div>
          {article.rsExtractionFileId != null && <div><a href={`https://docs.google.com/spreadsheets/d/${article.rsExtractionFileId}`} target="_blank" rel="noopener noreferrer">Extraction Sheet</a></div>}
        </div>
      )
    }
    if (key === "createdDate") {
      return formatDate(article.createdDate)
    }
    if (key === "lastModifiedDate") {
      return formatDate(article.lastModifiedDate)
    }
    // PR topic score — the raw classifier confidence (0.0–1.0). Render to a
    // fixed 3 decimals so scores line up visually; absent scores show a dash.
    if (key === "scorePRTopic") {
      const score = article.scorePRTopic
      return typeof score === "number" ? score.toFixed(3) : "—"
    }
    // Reports store institutions as a list; join it rather than String()-ing the array.
    if (key === "institutions") {
      return formatInstitutions(article.institutions) || "—"
    }
    // @ts-ignore
    const value = article[key]
    return value != null && value !== "" ? String(value) : "—"
  }

  // Total rendered column count, including the injected Remove column, used for
  // the full-width loading/empty rows' colSpan.
  const totalColumnCount: number =
    activeColumns.length + (showRemoveColumn ? 1 : 0)

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Pagination summary + toolbar row, directly above the table. */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        {/* Left: article-count summary + page navigation. */}
        <div className="flex gap-1 flex-wrap">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {loading ? (
              <div className="flex align-middle gap-2">
                <Spinner className="text-primary" />
                Loading...
              </div>
            ) : totalCount > 0 ? (
              `${fromItem.toLocaleString()}–${toItem.toLocaleString()} of ${totalCount.toLocaleString()} articles`
            ) : (
              "No articles"
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="xs"
              className="p-0 ml-2 -mr-1"
              onClick={goToFirstPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1 mr-1"
              onClick={goToPreviousPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4" /> Prev
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1"
              onClick={goToNextPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to next page"
            >
              Next <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-0 -ml-1"
              onClick={goToLastPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>

        {/* Right: rows-per-page, search, filters, view selector, trailing slot. */}
        <div className="flex items-center gap-2 flex-wrap">
          <TablePageSizeSelector
            size={String(pageSize)}
            onChange={changePageSize}
            options={PAGE_SIZE_OPTIONS}
          />
          <TableFiltersSelector
            filterOptions={filterOptions}
            onSave={changeFilters}
            initialFilters={activeFilters}
          />
          <TableViewSelector
            value={viewMode}
            onChange={setViewMode}
            options={viewModeOptions}
            columnGroups={columnGroupsForViewMode(viewMode)}
            visibleKeys={visibleColumns}
            fixedColumns={TOOLBAR_FIXED_COLUMNS}
            onSaveColumns={setVisibleColumns}
          />
          {toolbarTrailing}
        </div>
      </div>

      {/* The scrollable table itself. */}
      <div className="flex-1 min-h-0 overflow-auto border border-border/50">
      <Table className={loading ? "h-full" : ""}>
        <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
          <TableRow>
            {activeColumns.map(col => (
              <Fragment key={col.key}>
                <TableHead className={col.minWidth}>
                  {sortEnabled && SORTABLE_COLUMNS.has(col.key) ? (
                    <SortableColumnHeader
                      columnKey={col.key as JournalArticleSortableKey}
                      label={col.label}
                      sortConfig={sortConfig}
                      onToggleSort={onToggleSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
                {/* Inject the Remove column header right after Title. */}
                {showRemoveColumn && col.key === "title" && (
                  <TableHead className="min-w-[80px] w-[80px] text-center">
                    Remove
                  </TableHead>
                )}
              </Fragment>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {error !== null ? (
            <TableRow>
              <TableCell
                colSpan={totalColumnCount || 1}
                className="text-center text-destructive py-8"
              >
                Error loading articles: {error}
              </TableCell>
            </TableRow>
          ) : loading ? (
            <TableRow>
              <TableCell colSpan={totalColumnCount || 1}>
                <LoadingSpinner
                  loadingContent="Loading articles..."
                  width="w-[calc(100vw-var(--sidebar-width)-var(--secondary-sidebar-width))]"
                />
              </TableCell>
            </TableRow>
          ) : articles.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColumnCount || 1}
                className="text-center text-muted-foreground py-8"
              >
                No articles found.
              </TableCell>
            </TableRow>
          ) : (
            articles.map(article => (
              <TableRow
                key={article.sourceId}
                className={`hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer ${article.sourceId === selectedArticleId ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}
                onClick={() => onArticleSelect(article)}
              >
                {activeColumns.map(col => (
                  <Fragment key={col.key}>
                    <TableCell
                      className={CLAMP_KEYS.has(col.key) ? "max-w-[250px]" : "max-w-[300px]"}
                    >
                      {renderCellValue(article, col.key)}
                    </TableCell>
                    {/* Inject the Remove button cell right after Title. */}
                    {showRemoveColumn && col.key === "title" && (
                      <TableCell className="w-[80px] text-center">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          // Stop propagation so clicking Remove does not also
                          // open the article's detail panel via the row click.
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveClick(article)
                          }}
                          disabled={removingSourceId !== null}
                          aria-label={`Remove "${article.title || "article"}" from this collection`}
                          title="Remove from collection"
                          className="text-muted-foreground hover:text-destructive"
                        >
                          {removingSourceId === article.sourceId ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <X className="size-3.5" />
                          )}
                        </Button>
                      </TableCell>
                    )}
                  </Fragment>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  )
}
